import { IS_PROD } from "@/utils/env-constants";
import { ISOTimeString } from "@faro-lotv/ielement-types";

/**
 * The available tools in the Sphere Viewer codebase.
 * Used to determine which features are relevant on which page.
 */
export enum ViewerTool {
  sphereViewer = "sphereViewer",
  dataPreparationTool = "dataPreparationTool",
  legacyRegistrationTool = "legacyRegistrationTool",
}

/** Metadata required for each Sphere Viewer feature */
export type Feature = {
  /** A description of what this feature should enable/control */
  description: string;

  /** True to enable this feature by default in dev environments */
  isPreviewFeature: boolean;

  /** True to allow users to enable this feature manually */
  canBeUserEnabled: boolean;

  /**
   * True is an optional value can be associated with the enabled feature via the url (e.g. ?featureKey=value).
   * False if potential value in the url should be ignored.
   */
  supportValueInUrl: boolean;

  /**
   * Whether changing this feature requires a page reload to take effect.
   * Used for the features dialog.
   *
   * @default true
   */
  requiresReload?: boolean;

  /**
   * Date at which this feature was added
   */
  createdAt: ISOTimeString;

  /**
   * The tools this feature is relevant for.
   * Used in the features dialog to determine which features to show.
   */
  tools?: ViewerTool[];
};

/**
 * An object with all current features and their metadata
 *
 * NOTE: When you add a new feature flag please keep them sorted alphabetically to
 * reduce the risk of merge conflicts
 */
export const FEATURES = Object.freeze({
  AiChat: {
    canBeUserEnabled: true,
    description: "Allows access to the AI chat",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-03-24",
    requiresReload: false,
  },
  AiFindingsPolygon: {
    canBeUserEnabled: true,
    description:
      "Draw AI findings by their SAM3 masks or polygons instead of their boxes",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-09-02",
    requiresReload: false,
  },
  AllowViewerExport: {
    canBeUserEnabled: true,
    description:
      "Allows users to access supported export features with view permissions only",
    isPreviewFeature: false,
    supportValueInUrl: false,
    requiresReload: false,
    createdAt: "2025-08-08",
  },
  AutomaticWaypointMobileScanners: {
    canBeUserEnabled: true,
    description:
      "Enable automatic waypoint generation at 2 mts apart for mobile scanners",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-08-06",
    requiresReload: false,
  },
  BulkEdit: {
    canBeUserEnabled: true,
    description: "Enable bulk edit features in the project tree",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2025-11-26",
    requiresReload: false,
  },
  CadDebugTools: {
    canBeUserEnabled: true,
    description: "Enable some tools for debugging CAD/Model streaming",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-11-20",
  },
  CloudToCadHeatmapAvailable: {
    canBeUserEnabled: true,
    description: "Enable Heatmap feature button in WalkMode",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2024-02-08",
  },
  ColorByConnectivity: {
    canBeUserEnabled: true,
    description: "Color point clouds by their connectivity",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2025-12-02",
    tools: [ViewerTool.dataPreparationTool],
    requiresReload: false,
  },
  DarkMode: {
    canBeUserEnabled: true,
    description:
      "Applies the work-in-progress dark/system theme mode chosen in the Dev Tools",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-11-03",
    requiresReload: false,
  },
  DevTools: {
    canBeUserEnabled: false,
    description: "Enables debug tools that only developers should see",
    isPreviewFeature: !IS_PROD,
    supportValueInUrl: false,
    createdAt: "2023-11-07",
  },
  DisplaySignedDeviations: {
    canBeUserEnabled: true,
    description:
      "Display signed deviations along axis instead of absolute values",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-07-28",
  },
  DynamicOverviewMap: {
    canBeUserEnabled: true,
    description: "Enables selective rendering of the overview map",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-10-02",
  },
  FixAndRelease: {
    canBeUserEnabled: true,
    description:
      "Adds controls for fixing and releasing scans on publish to prevent unintentional changes to the registration",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-07-15",
    requiresReload: false,
    tools: [ViewerTool.dataPreparationTool],
  },
  FloorPlanBetaFormats: {
    canBeUserEnabled: true,
    description:
      "Allows user to download the generated floor plan in formats currently considered of beta-quality (DWG, RVT).",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2025-03-29",
  },
  FloorPlanVendor: {
    canBeUserEnabled: true,
    description:
      "Allows to change the vendor used for generating floor plans. See https://v2.project.api.dev.holobuilder.com/rapidoc#cmp--schemas-floorplanservicevendor",
    isPreviewFeature: false,
    supportValueInUrl: true,
    createdAt: "2025-06-26",
  },
  FullTree: {
    canBeUserEnabled: true,
    description: "Render the entire project tree and not the filtered version",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2023-11-03",
  },
  GaussianSplats: {
    canBeUserEnabled: true,
    description:
      "Enable the creation and rendering of datasets with Gaussian splatting",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2025-06-04",
  },
  ImportAnnotations: {
    canBeUserEnabled: true,
    description:
      "Allow to import annotations from a CSV generated by the Dashboard and edited by the user",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-08-05",
  },
  LargeCadModel: {
    canBeUserEnabled: true,
    description: "Enable the support for large CAD model",
    isPreviewFeature: true,
    // mandatory url value is the memory limit in MB (feature is ignored without a value)
    supportValueInUrl: true,
    createdAt: "2024-04-22",
  },
  OdometryWaypointSpacing: {
    canBeUserEnabled: true,
    description:
      "Enable a slider to set the minimum spacing between generated odometry waypoints",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-08-19",
    requiresReload: false,
  },
  OrderCadChunkByScreenSize: {
    canBeUserEnabled: true,
    description:
      "When StreamCadModelOutOfCore is enabled, enable the experimental sorting of chunks by size on screen (value = area in % of screen; default is 0.01)",
    isPreviewFeature: false,
    supportValueInUrl: true,
    createdAt: "2026-01-08",
  },
  PointerFeedback: {
    canBeUserEnabled: true,
    description:
      "Enable a feedback on pointer events in the 3D view over 3D data",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2024-01-12",
    reload: false,
  },
  PreAlignment: {
    canBeUserEnabled: true,
    description: "Enable automatic pre-alignment for orbis datasets",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2024-06-18",
  },
  RegistrationDev: {
    canBeUserEnabled: true,
    description: "Enable registration features for developers",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2024-04-03",
    tools: [ViewerTool.legacyRegistrationTool, ViewerTool.dataPreparationTool],
    requiresReload: false,
  },
  RenderingDebug: {
    canBeUserEnabled: true,
    description: "Rendering-related information and tools for developers",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-08-06",
    tools: [ViewerTool.dataPreparationTool],
    requiresReload: false,
  },
  RerunAssetDetection: {
    canBeUserEnabled: true,
    description:
      "Allow rerunning AI asset detection on the project even if it has already been run",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-10-22",
    requiresReload: false,
  },
  ShortShare: {
    canBeUserEnabled: true,
    description: "Replaces legacy share links with short share links",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2026-06-24",
  },
  SimpleScanOrganization: {
    canBeUserEnabled: true,
    description:
      "Adds further options to manage scans and clusters in the Draft View's project tree",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-04-01",
    requiresReload: false,
    tools: [ViewerTool.dataPreparationTool],
  },
  StreamCadModelOutOfCore: {
    canBeUserEnabled: true,
    description:
      "Loads the CAD geometry chunks in order of relevance for the current camera frustum",
    isPreviewFeature: true,
    supportValueInUrl: false,
    createdAt: "2025-08-04",
  },
  StreamCadModelOutOfCoreGeneration: {
    canBeUserEnabled: true,
    description:
      "Ask CAD importer backend to generate meshes optimized for out-of-core streaming",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-08-18",
  },
  TransformModel: {
    canBeUserEnabled: true,
    description: "Enable editing transformation on CAD Model",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2024-07-29",
  },
  Unassigned2dPhotos: {
    canBeUserEnabled: true,
    description: "Show unassigned 2D photos in the project",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-11-17",
  },
  UnlimitedUpload: {
    canBeUserEnabled: true,
    description: "Enable upload of point clouds larger than 50GB",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2025-01-24",
  },
  VisualRegistration: {
    canBeUserEnabled: true,
    description:
      "Allow users to manually align point clouds in the multi cloud view",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2024-06-12",
    tools: [ViewerTool.legacyRegistrationTool],
  },
  VlmAnalysis: {
    canBeUserEnabled: true,
    description:
      "Shows a debug button as part of the toolbar to investigate the VLM analysis of the current element",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2026-04-24",
    requiresReload: false,
  },
  WalkAnimation: {
    canBeUserEnabled: true,
    description: "Enable the use of the Sequence Walk Animation in WalkMode",
    isPreviewFeature: false,
    supportValueInUrl: false,
    createdAt: "2023-11-03",
  },
}) satisfies Readonly<Record<string, Feature>>;

/** A FeatureFlag is one of the keys of the FEATURE object */
export type FeatureFlag = keyof typeof FEATURES;

/**
 * An enum like object that contains a key/value pair for each valid Features to keep
 * the features code compatible with the previous implementation
 */
export const Features = computeFeaturesEnum();

/**
 * @returns all the valid features
 */
export function allFeatures(): FeatureFlag[] {
  return Object.keys(Features).filter(isFeatureFlag);
}

/**
 * @returns all the preview features
 */
export function previewFeatures(): FeatureFlag[] {
  return Object.entries(FEATURES)
    .filter(([, value]) => value.isPreviewFeature)
    .map(([key]) => key)
    .filter(isFeatureFlag);
}

/**
 * @returns true if the feature does allow optional value in the url; false means any value in the url would be ignored
 * @param feature FeatureFlag of the feature being tested
 */
export function isFeatureSupportingValue(feature: FeatureFlag): boolean {
  return FEATURES[feature].supportValueInUrl;
}

/**
 * @returns all the features the user can enable manually
 */
export function userControlledFeatures(): FeatureFlag[] {
  return Object.entries(FEATURES)
    .filter(([, value]) => value.canBeUserEnabled)
    .map(([key]) => key)
    .filter(isFeatureFlag);
}

/**
 * Type Guard to check if a string is one of the Features
 *
 * @param string to test
 * @returns true if it's one of the valid Features
 */
export function isFeatureFlag(string: string): string is FeatureFlag {
  return Object.keys(Features).includes(string);
}

/** An object that act like an Enum, where each value is both the key and the value of the object */
type FeaturesEnum = {
  [key in keyof typeof FEATURES]: keyof typeof FEATURES;
};

/** @returns an EnumLike object with a key for each Feature in the FEATURES object */
function computeFeaturesEnum(): FeaturesEnum {
  // Object.keys does not narrow down a string to a key so we need to use a type assertion
  // the validity of the returned object is checked in the Features unit test
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return Object.keys(FEATURES).reduce(
    (prev, key) => Object.assign(prev, { [key]: key }),
    {},
  ) as FeaturesEnum;
}

/** Describe a query to enable a feature in the url. */
type FeatureQuery = {
  /** feature being enabled. */
  featureFlag: FeatureFlag;
  /** actual query included in the url (the url is NOT case sensitive = urlQuery might be different from featureFlag). */
  urlQuery: string;
  /** Optional value associate with the feature. */
  value: string | null;
  /** True if the feature is explicitly set to `false` and doesn't support a value. */
  disabled: boolean;
};

/**
 * Extract the list of features and optional values from the url (only for feature supporting values).
 *
 * @param searchParams URLSearchParams storing the whole url
 * @returns a list of triplet with enabled FeatureFlag, the feature name in the url, and the optional value
 * The url is not case sensitive, so the feature name in the url may have different case than the FeatureFlag.
 */
export function parseFeaturesAndValuesFromUrl(
  searchParams: URLSearchParams,
): FeatureQuery[] {
  // map storing the case sensitive FeatureFlag from each lower case key (required because the url is not case sensitive)
  const lowerCaseFeatureFlags = new Map<string, FeatureFlag>();
  for (const feature of userControlledFeatures()) {
    lowerCaseFeatureFlags.set(feature.toLowerCase(), feature);
  }

  const urlParams = new Array<FeatureQuery>();
  searchParams.forEach((urlValue, urlQuery) => {
    const lowerCaseKey = urlQuery.toLowerCase();
    const featureFlag = lowerCaseFeatureFlags.get(lowerCaseKey);
    if (featureFlag !== undefined) {
      // If the feature doesn't support values, it can be explicitly disabled by setting it to "false"
      const disabled =
        urlValue.toLowerCase() === "false" &&
        !isFeatureSupportingValue(featureFlag);
      const isEmpty =
        urlValue === "" ||
        (!isFeatureSupportingValue(featureFlag) && !disabled);

      urlParams.push({
        featureFlag,
        urlQuery,
        value: isEmpty ? null : urlValue,
        disabled,
      });
    }
  });

  return urlParams;
}
